<!--
  数据管理后台主页
  用途：管理商品、采购实例、类别（增删改、上传、导入导出）
  用法：登录后访问 /#/admin
-->
<template>
  <div class="dashboard">
    <div class="toolbar">
      <div class="tabs">
        <button type="button" :class="{ active: tab === 'products' }" @click="switchTab('products')">
          商品管理
        </button>
        <button type="button" :class="{ active: tab === 'cases' }" @click="switchTab('cases')">
          采购实例
        </button>
        <button
          type="button"
          :class="{ active: tab === 'categories' }"
          @click="switchTab('categories')"
        >
          类别管理
        </button>
      </div>
      <div class="toolbar-actions">
        <button type="button" class="ghost" @click="onExport">导出 JSON</button>
        <label class="ghost file-btn">
          导入 JSON
          <input type="file" accept="application/json" hidden @change="onImport" />
        </label>
        <button type="button" class="primary" @click="openCreate">新增</button>
      </div>
    </div>

    <p v-if="loading" class="state">加载中…</p>
    <p v-else-if="message" class="state msg">{{ message }}</p>

    <div v-else class="table-wrap">
      <table>
        <thead>
          <tr>
            <th>ID</th>
            <th>{{ tab === 'categories' ? '中文名称' : '标题' }}</th>
            <th>{{ tab === 'categories' ? '英文名称' : '类别' }}</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in list" :key="item.id">
            <td>{{ item.id }}</td>
            <td>{{ displayTitle(item) }}</td>
            <td>{{ displaySecondCol(item) }}</td>
            <td class="ops">
              <button type="button" @click="openEdit(item)">编辑</button>
              <button type="button" class="danger" @click="onDelete(item)">删除</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 编辑弹层 -->
    <div v-if="editing" class="modal">
      <div class="modal-card">
        <div class="modal-head">
          <h2>{{ isCreate ? '新增' : '编辑' }}{{ tabLabel }}</h2>
          <button type="button" class="ghost" @click="editing = false">关闭</button>
        </div>

        <!-- 类别表单 -->
        <template v-if="tab === 'categories'">
          <div class="form-grid">
            <label class="full">
              <span>类别 ID（英文键名，如 kitchen）</span>
              <input v-model="form.id" :disabled="!isCreate && !allowRename" placeholder="例如 homeGoods" />
              <small v-if="!isCreate" class="hint">
                <label class="inline">
                  <input v-model="allowRename" type="checkbox" />
                  允许修改 ID（会同步更新商品/案例引用）
                </label>
              </small>
            </label>
          </div>
          <div class="lang-tabs">
            <button
              v-for="lang in langs"
              :key="lang"
              type="button"
              :class="{ active: editLang === lang }"
              @click="editLang = lang"
            >
              {{ langLabel(lang) }}
            </button>
          </div>
          <div class="form-grid">
            <label class="full">
              <span>类别名称（{{ langLabel(editLang) }}）</span>
              <input v-model="form.i18n[editLang].name" placeholder="请输入类别名称" />
            </label>
          </div>
        </template>

        <!-- 商品 / 案例表单 -->
        <template v-else>
          <div class="form-grid">
            <label>
              <span>ID（留空自动生成）</span>
              <input v-model="form.id" :disabled="!isCreate" />
            </label>
            <label>
              <span>类别</span>
              <select v-model="form.categoryKey">
                <option v-if="!categoryOptions.length" value="" disabled>暂无类别，请先在「类别管理」中新增</option>
                <option v-for="cat in categoryOptions" :key="cat.id" :value="cat.id">
                  {{ cat.label }}（{{ cat.id }}）
                </option>
              </select>
            </label>

            <template v-if="tab === 'products'">
              <label>
                <span>价格</span>
                <input v-model="form.price" />
              </label>
              <label>
                <span>主图 URL</span>
                <input v-model="form.src" />
                <button type="button" class="mini" @click="uploadTo('src')">上传主图</button>
              </label>
            </template>
            <template v-else>
              <label>
                <span>年份</span>
                <input v-model="form.year" />
              </label>
              <label>
                <span>封面 URL</span>
                <input v-model="form.cover" />
                <button type="button" class="mini" @click="uploadTo('cover')">上传封面</button>
              </label>
              <label>
                <span>成本优化</span>
                <input v-model="form.metrics.saving" />
              </label>
              <label>
                <span>交付数量</span>
                <input v-model="form.metrics.units" />
              </label>
              <label>
                <span>交付周期</span>
                <input v-model="form.metrics.leadTime" />
              </label>
            </template>
          </div>

          <div class="lang-tabs">
            <button
              v-for="lang in langs"
              :key="lang"
              type="button"
              :class="{ active: editLang === lang }"
              @click="editLang = lang"
            >
              {{ langLabel(lang) }}
            </button>
          </div>

          <div class="form-grid" v-if="tab === 'products'">
            <label>
              <span>名称</span>
              <input v-model="form.i18n[editLang].name" />
            </label>
            <label>
              <span>起订量</span>
              <input v-model="form.i18n[editLang].moq" />
            </label>
            <label>
              <span>材质</span>
              <input v-model="form.i18n[editLang].material" />
            </label>
            <label>
              <span>交期</span>
              <input v-model="form.i18n[editLang].leadTime" />
            </label>
            <label class="full">
              <span>描述</span>
              <textarea v-model="form.i18n[editLang].description" rows="3" />
            </label>
            <label class="full">
              <span>卖点（每行一条）</span>
              <textarea v-model="specsText" rows="3" />
            </label>
          </div>

          <div class="form-grid" v-else>
            <label class="full">
              <span>标题</span>
              <input v-model="form.i18n[editLang].title" />
            </label>
            <label class="full">
              <span>客户</span>
              <input v-model="form.i18n[editLang].client" />
            </label>
            <label class="full">
              <span>摘要</span>
              <textarea v-model="form.i18n[editLang].summary" rows="2" />
            </label>
            <label class="full">
              <span>挑战</span>
              <textarea v-model="form.i18n[editLang].challenge" rows="2" />
            </label>
            <label class="full">
              <span>方案</span>
              <textarea v-model="form.i18n[editLang].solution" rows="2" />
            </label>
            <label class="full">
              <span>成果</span>
              <textarea v-model="form.i18n[editLang].result" rows="2" />
            </label>
            <label class="full">
              <span>亮点（每行一条）</span>
              <textarea v-model="highlightsText" rows="3" />
            </label>
          </div>

          <div class="gallery-box">
            <div class="gallery-head">
              <strong>媒体画廊（图片/视频）</strong>
              <div>
                <button type="button" class="mini" @click="uploadGallery('image')">上传图片</button>
                <button type="button" class="mini" @click="uploadGallery('video')">上传视频</button>
              </div>
            </div>
            <div v-for="(media, index) in form.gallery" :key="index" class="gallery-row">
              <select v-model="media.type">
                <option value="image">图片</option>
                <option value="video">视频</option>
              </select>
              <input v-model="media.src" placeholder="src" />
              <input v-model="media.poster" placeholder="poster（视频封面）" />
              <button type="button" class="danger" @click="form.gallery.splice(index, 1)">删</button>
            </div>
          </div>
        </template>

        <div class="modal-foot">
          <button type="button" class="ghost" @click="editing = false">取消</button>
          <button type="button" class="primary" :disabled="saving" @click="onSave">
            {{ saving ? '保存中…' : '保存' }}
          </button>
        </div>
        <p v-if="formError" class="error">{{ formError }}</p>
      </div>
    </div>

    <input ref="fileInput" type="file" hidden @change="onFilePicked" />
  </div>
</template>

<script setup>
/**
 * 后台数据管理逻辑
 * 用法：切换 tab 加载列表；类别支持中英西文名称；商品/案例下拉显示中文类别名
 */
import { computed, onMounted, reactive, ref } from 'vue'
import {
  createCase,
  createCategory,
  createProduct,
  deleteCase,
  deleteCategory,
  deleteProduct,
  exportResource,
  fetchCases,
  fetchCategories,
  fetchProducts,
  importResource,
  updateCase,
  updateCategory,
  updateProduct,
  uploadFile
} from '@/services/api'
import { categoryLabel } from '@/utils/localize'

const tab = ref('products')
const list = ref([])
const categories = ref([])
const loading = ref(false)
const message = ref('')
const editing = ref(false)
const isCreate = ref(false)
const saving = ref(false)
const formError = ref('')
const editLang = ref('zh-CN')
const allowRename = ref(false)
/** 编辑类别时记录原 ID，用于改名后的 PUT 路径 */
const originalCategoryId = ref('')
const langs = ['zh-CN', 'en', 'es']

const fileInput = ref(null)
const uploadTarget = ref(null)

const tabLabel = computed(() => {
  if (tab.value === 'products') return '商品'
  if (tab.value === 'cases') return '采购实例'
  return '类别'
})

/** 下拉选项：中文名 + 英文键 */
const categoryOptions = computed(() =>
  categories.value.map((c) => ({
    id: c.id,
    label: categoryLabel(c, 'zh-CN') || c.id
  }))
)

const langLabel = (lang) => {
  if (lang === 'zh-CN') return '中文'
  if (lang === 'en') return 'English'
  return 'Español'
}

const emptyProductI18n = () => ({
  name: '',
  moq: '',
  material: '',
  leadTime: '',
  description: '',
  specs: []
})

const emptyCaseI18n = () => ({
  title: '',
  client: '',
  summary: '',
  challenge: '',
  solution: '',
  result: '',
  highlights: []
})

const emptyCategoryI18n = () => ({ name: '' })

const form = reactive(createEmptyForm('products'))

function ensureLangBuckets(kind) {
  const factory =
    kind === 'categories'
      ? emptyCategoryI18n
      : kind === 'products'
        ? emptyProductI18n
        : emptyCaseI18n
  form.i18n = form.i18n || {}
  langs.forEach((lang) => {
    form.i18n[lang] = { ...factory(), ...(form.i18n[lang] || {}) }
  })
}

function createEmptyForm(kind) {
  if (kind === 'categories') {
    return {
      id: '',
      i18n: {
        'zh-CN': emptyCategoryI18n(),
        en: emptyCategoryI18n(),
        es: emptyCategoryI18n()
      }
    }
  }
  if (kind === 'products') {
    return {
      id: '',
      categoryKey: categories.value[0]?.id || '',
      price: '',
      src: '',
      gallery: [],
      i18n: {
        'zh-CN': emptyProductI18n(),
        en: emptyProductI18n(),
        es: emptyProductI18n()
      }
    }
  }
  return {
    id: '',
    categoryKey: categories.value[0]?.id || '',
    cover: '',
    year: String(new Date().getFullYear()),
    metrics: { saving: '', units: '', leadTime: '' },
    gallery: [],
    i18n: {
      'zh-CN': emptyCaseI18n(),
      en: emptyCaseI18n(),
      es: emptyCaseI18n()
    }
  }
}

function assignForm(data) {
  Object.keys(form).forEach((k) => delete form[k])
  Object.assign(form, JSON.parse(JSON.stringify(data)))
  ensureLangBuckets(tab.value)
  if (tab.value !== 'categories') {
    if (!Array.isArray(form.gallery)) form.gallery = []
    form.gallery = form.gallery.map((g) =>
      typeof g === 'string' ? { type: 'image', src: g, poster: '' } : { type: 'image', poster: '', ...g }
    )
    if (!form.metrics) form.metrics = { saving: '', units: '', leadTime: '' }
  }
}

const specsText = computed({
  get: () => (form.i18n?.[editLang.value]?.specs || []).join('\n'),
  set: (val) => {
    form.i18n[editLang.value].specs = String(val)
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean)
  }
})

const highlightsText = computed({
  get: () => (form.i18n?.[editLang.value]?.highlights || []).join('\n'),
  set: (val) => {
    form.i18n[editLang.value].highlights = String(val)
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean)
  }
})

const displayTitle = (item) => {
  if (tab.value === 'categories') return categoryLabel(item, 'zh-CN') || item.id
  const zh = item.i18n?.['zh-CN'] || {}
  return zh.name || zh.title || item.id
}

const displaySecondCol = (item) => {
  if (tab.value === 'categories') return categoryLabel(item, 'en') || '-'
  return categoryLabel(
    categories.value.find((c) => c.id === item.categoryKey),
    'zh-CN'
  ) || item.categoryKey || '-'
}

const loadCategories = async () => {
  categories.value = await fetchCategories()
}

const loadList = async () => {
  loading.value = true
  message.value = ''
  try {
    await loadCategories()
    if (tab.value === 'products') list.value = await fetchProducts()
    else if (tab.value === 'cases') list.value = await fetchCases()
    else list.value = categories.value
  } catch (e) {
    message.value = e.message || '加载失败'
    list.value = []
  } finally {
    loading.value = false
  }
}

const switchTab = async (next) => {
  tab.value = next
  editing.value = false
  allowRename.value = false
  await loadList()
}

const openCreate = () => {
  if (tab.value !== 'categories' && !categories.value.length) {
    alert('请先在「类别管理」中新增至少一个类别')
    return
  }
  isCreate.value = true
  allowRename.value = false
  originalCategoryId.value = ''
  assignForm(createEmptyForm(tab.value))
  editLang.value = 'zh-CN'
  formError.value = ''
  editing.value = true
}

const openEdit = (item) => {
  isCreate.value = false
  allowRename.value = false
  originalCategoryId.value = item.id
  assignForm(item)
  editLang.value = 'zh-CN'
  formError.value = ''
  editing.value = true
}

const onSave = async () => {
  saving.value = true
  formError.value = ''
  try {
    const payload = JSON.parse(JSON.stringify(form))
    delete payload._originalId
    if (tab.value === 'categories') {
      if (!payload.id) throw new Error('请填写类别 ID')
      if (!payload.i18n?.['zh-CN']?.name) throw new Error('请至少填写中文名称')
      if (isCreate.value) await createCategory(payload)
      else await updateCategory(originalCategoryId.value || payload.id, payload)
    } else {
      if (!payload.categoryKey) throw new Error('请选择类别')
      if (!payload.id) delete payload.id
      if (tab.value === 'products') {
        if (isCreate.value) await createProduct(payload)
        else await updateProduct(payload.id, payload)
      } else {
        if (isCreate.value) await createCase(payload)
        else await updateCase(payload.id, payload)
      }
    }
    editing.value = false
    await loadList()
  } catch (e) {
    formError.value = e.message || '保存失败'
  } finally {
    saving.value = false
  }
}

const onDelete = async (item) => {
  const name = displayTitle(item)
  if (!confirm(`确认删除「${name}」？`)) return
  try {
    if (tab.value === 'products') await deleteProduct(item.id)
    else if (tab.value === 'cases') await deleteCase(item.id)
    else await deleteCategory(item.id)
    await loadList()
  } catch (e) {
    alert(e.message || '删除失败')
  }
}

const onExport = async () => {
  try {
    await exportResource(tab.value)
  } catch (e) {
    alert(e.message || '导出失败')
  }
}

const onImport = async (event) => {
  const file = event.target.files?.[0]
  event.target.value = ''
  if (!file) return
  try {
    const text = await file.text()
    const items = JSON.parse(text)
    if (!Array.isArray(items)) throw new Error('JSON 必须是数组')
    const mode = confirm('确定覆盖现有数据？\n确定=覆盖，取消=合并') ? 'replace' : 'merge'
    await importResource(tab.value, items, mode)
    await loadList()
    alert('导入成功')
  } catch (e) {
    alert(e.message || '导入失败')
  }
}

const uploadTo = (field) => {
  uploadTarget.value = { kind: 'field', field }
  fileInput.value?.click()
}

const uploadGallery = (preferType) => {
  uploadTarget.value = { kind: 'gallery', preferType }
  fileInput.value?.click()
}

const onFilePicked = async (event) => {
  const file = event.target.files?.[0]
  event.target.value = ''
  if (!file || !uploadTarget.value) return
  try {
    const data = await uploadFile(file)
    if (uploadTarget.value.kind === 'field') {
      form[uploadTarget.value.field] = data.url
    } else {
      form.gallery.push({
        type: data.type || uploadTarget.value.preferType || 'image',
        src: data.url,
        poster: data.type === 'image' ? data.url : form.cover || form.src || ''
      })
    }
  } catch (e) {
    alert(e.message || '上传失败')
  }
}

onMounted(loadList)
</script>

<style scoped>
.toolbar {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
}

.tabs,
.toolbar-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tabs button,
.ghost,
.primary,
.mini,
.ops button {
  border: 1px solid var(--color-border);
  background: #fff;
  border-radius: 999px;
  padding: 8px 14px;
  font-size: 13px;
  cursor: pointer;
}

.tabs button.active,
.primary {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: #fff;
}

.file-btn {
  display: inline-flex;
  align-items: center;
}

.state {
  padding: 24px;
  color: var(--color-text-secondary);
}

.table-wrap {
  background: #fff;
  border: 1px solid var(--color-border);
  border-radius: 12px;
  overflow: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

th,
td {
  padding: 12px 14px;
  border-bottom: 1px solid var(--color-border);
  text-align: left;
}

.ops {
  display: flex;
  gap: 8px;
}

.danger {
  color: var(--color-primary) !important;
}

.modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 16px;
  overflow: auto;
  z-index: 200;
}

.modal-card {
  width: min(860px, 100%);
  background: #fff;
  border-radius: 16px;
  padding: 20px;
}

.modal-head,
.modal-foot,
.gallery-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 14px;
}

label {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  color: var(--color-text-secondary);
}

label.full {
  grid-column: 1 / -1;
}

label.inline {
  flex-direction: row;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
}

.hint {
  color: var(--color-text-secondary);
  font-size: 12px;
}

input,
textarea,
select {
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
}

.lang-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.lang-tabs button.active {
  background: var(--color-primary-soft);
  color: var(--color-primary);
  border-color: var(--color-primary);
}

.gallery-box {
  border: 1px solid var(--color-border);
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 14px;
}

.gallery-row {
  display: grid;
  grid-template-columns: 90px 1fr 1fr auto;
  gap: 8px;
  margin-bottom: 8px;
}

.mini {
  margin-top: 6px;
  padding: 6px 10px;
}

.error {
  color: var(--color-primary);
  margin-top: 8px;
}

@media (max-width: 720px) {
  .form-grid,
  .gallery-row {
    grid-template-columns: 1fr;
  }
}
</style>
